import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { RiCloseLine } from "react-icons/ri";

const LOCAL_STORAGE_KEY = "estore_popup_seen_v1";

export default function EstorePopup() {
  const [popup, setPopup] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hasSeen = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (hasSeen) return;

    const controller = new AbortController();

    const loadPopup = async () => {
      try {
        const res = await fetch("/api/estore/popups", {
          signal: controller.signal,
        });
        if (!res.ok) return;

        const json = await res.json();
        const data = json?.data;

        if (!data || data.is_active === false) return;

        setPopup(data);
        setIsOpen(true);
      } catch (error) {
        // Abaikan error (network/abort) agar tidak ganggu UX
      }
    };

    loadPopup();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") handleClose();
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const markSeen = () => {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(LOCAL_STORAGE_KEY, "true");
      } catch {
        // abaikan jika localStorage tidak tersedia
      }
    }
  };

  const handleClose = () => {
    markSeen();
    setIsOpen(false);
  };

  const handleClickCta = () => {
    markSeen();
    if (popup?.url && typeof window !== "undefined") {
      window.location.href = popup.url;
    }
  };

  if (!isOpen || !popup) return null;

  return (
    <div style={overlayStyle}>
      <div style={backdropStyle} onClick={handleClose} />
      <div
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        aria-label={popup.name || "Informasi toko"}
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={handleClose}
          aria-label="Tutup popup"
          className="absolute right-3 top-3 z-20 flex h-11 w-11 shrink-0 items-center justify-center p-0 rounded-full border-2 border-white/80 bg-nevada-950 text-white shadow-xl transition hover:scale-105 hover:bg-black focus:outline-none focus:ring-4 focus:ring-white/80"
        >
          <RiCloseLine
            aria-hidden="true"
            className="block h-7 w-7 shrink-0"
          />
        </button>
        <div style={contentStyle}>
          <Image
            src={popup.thumbnail}
            alt={popup.name || "Promo banner popup"}
            width={300}
            height={400}
            sizes="(max-width: 480px) 280px, (max-width: 768px) 300px, 300px"
            priority
            fetchPriority="high"
            placeholder="blur"
            blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "400px",
              objectFit: "contain",
            }}
          />
          {popup.url && (
            <button
              type="button"
              onClick={handleClickCta}
              className="bg-nevada-800 text-white hover:bg-nevada-900 rounded-full px-4 py-2 my-2"
            >
              Kunjungi Halaman
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const backdropStyle = {
  position: "absolute",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
};

const modalStyle = {
  position: "relative",
  zIndex: 1,
  maxWidth: "90vw",
  maxHeight: "90vh",
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
  display: "flex",
  flexDirection: "column",
};

const contentStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};
